import React, { createContext, useContext, useState, useEffect } from 'react';
import { isAuthenticated, getCurrentUser, logout as clearAuth } from '../lib/authUtils';
import { apiClient } from '../lib/apiClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuth, setIsAuth] = useState(false);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = window.localStorage.getItem('access_token') || window.localStorage.getItem('b2b-jwtToken');
        const storedUser = getCurrentUser();
        
        if (token && storedUser) {
          setUser(storedUser);
          setIsAuth(true);
          
          // Optionally fetch fresh user data
          try {
            const userDetails = await fetchUserDetails();
            if (userDetails) {
              setUser(userDetails);
              window.localStorage.setItem('user', JSON.stringify(userDetails));
            }
          } catch (error) {
            console.warn('Failed to fetch user details:', error);
            // Keep stored user if fetch fails
          }
        } else {
          setUser(null);
          setIsAuth(false);
        }
      } catch (error) {
        console.error('Error loading user:', error);
        setUser(null);
        setIsAuth(false);
      } finally {
        setLoading(false);
      }
    };

    loadUser();

    // Listen for auth state changes
    const handleAuthChange = () => {
      const token = window.localStorage.getItem('access_token') || window.localStorage.getItem('b2b-jwtToken');
      const storedUser = getCurrentUser();
      setIsAuth(!!token);
      setUser(storedUser);
    };

    window.addEventListener('authStateChanged', handleAuthChange);

    return () => {
      window.removeEventListener('authStateChanged', handleAuthChange);
    };
  }, []);

  const fetchUserDetails = async () => {
    try {
      const token = window.localStorage.getItem('access_token') || window.localStorage.getItem('b2b-jwtToken');
      if (!token) return null;

      // Decode JWT to get userId (simple decode, not verification)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId = payload.id;

      if (!userId) return null;

      const response = await apiClient.get('/usermgmt/getByCustomerByUserId');
      if (response.data && response.data.success) {
        return response.data.data || response.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user details:', error);
      return null;
    }
  };

  const login = (userData, token) => {
    // Store token
    if (token) {
      window.localStorage.setItem('b2b-jwtToken', token);
      window.localStorage.setItem('access_token', token);
    }

    // Store user data
    if (userData) {
      window.localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    }

    setIsAuth(true);
    
    // Trigger auth state change event
    window.dispatchEvent(new CustomEvent('authStateChanged'));
  };

  const logout = (navigate = null) => {
    clearAuth(navigate);
    setUser(null);
    setIsAuth(false);
    
    // Trigger auth state change event
    window.dispatchEvent(new CustomEvent('authStateChanged'));
  };

  const updateUser = (userData) => {
    if (userData) {
      window.localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      window.dispatchEvent(new CustomEvent('authStateChanged'));
    }
  };

  const value = {
    user,
    isAuthenticated: isAuth,
    loading,
    login,
    logout,
    updateUser,
    fetchUserDetails,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


